<template>
  <StudioFeedRowItem
    v-if="resolvedComponentId > 0 && completionCardContextGatePasses"
    :dynamic-component-id="resolvedComponentId"
    :entity-id="storyEntityId"
    :context-name="resolvedCardStoryContextName"
    :scoped-contexts="resolvedCardScopedContexts"
    :theme-mode="themeMode"
    :edit-mode="editMode"
    size-to-card
  />
  <BrokenContextElement
    v-else-if="resolvedCardContextGaps.length"
    heading="Completion card context not bound"
    :component-name="String(resolvedComponentId)"
    :problems="resolvedCardContextProblemMessages"
  />
  <iText
    v-else-if="editMode"
    variant="subtle"
    font-size="small"
    data-test="completion-empty-state"
  >
    No component set for the “{{ currentStatusLabel }}” status.
  </iText>
</template>

<script>
import { BaseStoryCardElementMixin }
  from "%/dynamic-component-editor/standard-components/base-story-card/base-story-card-element-mixin";
import StudioFeedRowItem from "%/dynamic-component-editor/standard-elements/StudioFeedRowItem.vue";
import BrokenContextElement from "%/dynamic-component-editor/components/BrokenContextElement.vue";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { useCompletionAssignmentStore } from "%/dynamic-component-editor/stores/completion-assignment-store";
import { useCompletionStatusStore }
  from "%/dynamic-component-editor/stores/completion-status-store";
import { resolveCompletionStatus } from "%/dynamic-component-editor/helpers/completion-status-resolver";
import { useCompletionPreviewStore } from "%/dynamic-component-editor/stores/completion-preview-store";
import { normalizePreviewStatus, PREVIEW_STATE_LABELS }
  from "%/dynamic-component-editor/helpers/completion-preview-states";
import {
  CompletionCardVariantNotStarted,
  CompletionCardVariantStarted,
  CompletionCardVariantComplete,
  COMPLETION_CARD_OPTION_BY_STATUS,
} from "%/dynamic-component-editor/options/completion-card-variant";
import { ELEMENT_PANEL_FOLDER } from "%/dynamic-component-editor/constants/element-panel-folder-names";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import CompletionCardContextBindings from "%/dynamic-component-editor/options/completion-card-context-bindings";
import { CompletionCardContextMixin }
  from "%/dynamic-component-editor/standard-components/base-story-card/completion-card-context-mixin";

export default {
  name: "CompletionStatus",
  elementPanelSettings: {
    folder: ELEMENT_PANEL_FOLDER.COMPLETION,
    displayName: "Completion Status",
    displayIcon: "mdi:list-status",
    elementGroup: "storyElements",
    elementType: "element",
  },
  options: [
    CompletionCardVariantNotStarted,
    new CompletionCardVariantStarted({}, true),
    new CompletionCardVariantComplete({}, true),
    new CompletionCardContextBindings({}, true),
  ],
  optionBundles: [
    new LayoutWidthHeightOptions({
      "default.layout-width": "hug",
      "default.layout-height": "hug",
    }, {
      "layout-width": true,
      "layout-height": true,
      "max-width": true,
      "max-height": true,
      "min-width": true,
      "min-height": true,
    }),
  ],
  components: { StudioFeedRowItem, BrokenContextElement },
  mixins: [BaseStoryCardElementMixin, CompletionCardContextMixin],
  computed: {
    completionPublicationId() {
      return useConfigurationStore().publicationId;
    },
    currentStatus() {
      if (this.editMode || this.isPreviewMode) {
        return normalizePreviewStatus(
          useCompletionPreviewStore().previewStatus,
          useCompletionAssignmentStore().isTwoStateMode(this.completionPublicationId),
        );
      }
      const store = useCompletionStatusStore();
      const rawStatus = resolveCompletionStatus({
        entity: this.entity,
        storyId: this.storyEntityId,
        statusByStory: store.statusByStory,
        progressByFolder: store.progressByFolder,
      });
      return useCompletionAssignmentStore().normalizeStatus(this.completionPublicationId, rawStatus);
    },
    currentStatusLabel() {
      return PREVIEW_STATE_LABELS[this.currentStatus] ?? this.currentStatus;
    },
    // XXX: ONE system. The per-element option is the ONLY source of the bound
    // XXX: card. The publication-level slot map SEEDS this at drop time and is
    // XXX: read by the assign-components picker -- it is NOT a render-time
    // XXX: fallback. Two competing resolution paths is how an author could
    // XXX: change a card here and see it silently overridden there.
    resolvedComponentId() {
      const optionClass = COMPLETION_CARD_OPTION_BY_STATUS[this.currentStatus];
      return optionClass ? Number(this.getOptionValue(optionClass)) || 0 : 0;
    },
    // XXX: Removing the fallback makes "no card bound" reachable for elements
    // XXX: authored before the seed existed. That is a BROKEN STATE and must be
    // XXX: loud -- rendering nothing is how it would go unnoticed on a long page.
    completionBindingProblem() {
      if (this.resolvedComponentId > 0) {
        return "";
      }
      return `No card is bound for the "${this.currentStatus}" completion state on this element.`;
    },
  },
  watch: {
    completionPublicationId: {
      immediate: true,
      handler(publicationId) {
        if (publicationId) {
          useCompletionAssignmentStore().ensureAssignments(publicationId);
          useCompletionAssignmentStore().ensureTwoStateMode(publicationId);
        }
      },
    },
    storyEntityId: {
      immediate: true,
      handler(storyId) {
        if (!this.editMode && storyId) {
          useCompletionStatusStore().ensureFolderProgress(storyId);
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
